import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  Building2,
  Crown,
  ChevronRight,
  Briefcase,
  ShieldCheck,
  Archive,
  RotateCcw,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import BackNavigationButton from "../components/navigation/BackNavigationButton";
import { useAuth } from "../App";
import { toast } from "sonner";

import { api } from "../services/apiClient";
import {
  getDerivedPaymentStatus,
  getPlanBadgeClass,
  getPlanLabel,
  getRoleBadgeClass,
  getRoleLabel,
  getStatusBadgeClass,
  getStatusLabel,
} from "../constants/plans";

const AdminUsers = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [showArchivedUsers, setShowArchivedUsers] = useState(false);
  const ITEMS_PER_PAGE = 50;

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/dashboard");
      return;
    }
    fetchUsers();
  }, [currentPage, showArchivedUsers]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/admin/users?page=${currentPage}&limit=${ITEMS_PER_PAGE}&include_archived=${showArchivedUsers}`,
      );
      const data = await response.json();
      // Handle both paginated and legacy response
      if (data.users) {
        setUsers(data.users);
        setTotalPages(data.total_pages || 1);
        setTotalUsers(data.total || data.users.length);
      } else if (Array.isArray(data)) {
        setUsers(data);
        setTotalUsers(data.length);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveUser = async (userId) => {
    if (!window.confirm("¿Archivar este usuario? Se ocultará sin borrar su historial.")) return;
    try {
      const response = await api.patchQuery(`/admin/users/${userId}/archive`);
      if (!response.ok) {
        const e = await response.json().catch(() => ({}));
        throw new Error(e.detail || "No se pudo archivar el usuario");
      }
      toast.success("Usuario archivado");
      fetchUsers();
    } catch (error) {
      toast.error(error.message || "Error al archivar usuario");
    }
  };

  const handleRestoreUser = async (userId) => {
    try {
      const response = await api.patchQuery(`/admin/users/${userId}/restore`);
      if (!response.ok) {
        const e = await response.json().catch(() => ({}));
        throw new Error(e.detail || "No se pudo restaurar el usuario");
      }
      toast.success("Usuario restaurado");
      fetchUsers();
    } catch (error) {
      toast.error(error.message || "Error al restaurar usuario");
    }
  };

  const getRoleIcon = (role) => {
    if (role === "admin") return Crown;
    if (role === "developer") return Building2;
    if (role === "broker") return Briefcase;
    return User;
  };

  return (
    <div className="min-h-screen bg-muted">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 md:py-8">
        <div className="mb-6 md:mb-8">
          <div className="mb-4">
            <BackNavigationButton dataTestId="admin-users-back" />
          </div>
          <h1
            className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight"
            style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
          >
            Gestión de Usuarios
          </h1>
          <p className="text-sm md:text-base text-muted-foreground">
            {users.length} usuarios registrados en la plataforma
          </p>
          <div className="mt-3">
            <Button
              variant={showArchivedUsers ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setCurrentPage(1);
                setShowArchivedUsers((v) => !v);
              }}
            >
              {showArchivedUsers ? "Ver usuarios activos" : "Ver usuarios archivados"}
            </Button>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
            <p className="text-sm">
              ℹ️ <strong>Nota:</strong> El plan ahora depende del rol. Esta
              vista ya no permite mezclar roles y planes manualmente para evitar
              inconsistencias.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            <div className="bg-background rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted border-b border-border">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">
                        Usuario
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">
                        Rol
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">
                        Plan
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">
                        Estado
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">
                        Propiedades
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">
                        Desarrollos
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">
                        Regla
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">
                        Acción
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {users.map((usr) => {
                      const RoleIcon = getRoleIcon(usr.role);
                      return (
                        <tr key={usr.id} className="hover:bg-muted/50">
                          <td className="px-4 py-4">
                            <div>
                              <p className="font-semibold text-sm">
                                {usr.name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {usr.email}
                              </p>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <span
                              className={`px-3 py-1 text-xs font-semibold rounded-full inline-flex items-center gap-1 ${getRoleBadgeClass(usr.role)}`}
                            >
                              <RoleIcon className="w-3 h-3" />
                              {getRoleLabel(usr.role, "short")}
                            </span>
                            {usr.is_verified && (
                              <span className="ml-2 text-xs text-green-600">
                                ✓ Verificado
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <span
                              className={`px-3 py-1 text-xs font-semibold rounded-full ${getPlanBadgeClass(usr.subscription_plan)}`}
                            >
                              {getPlanLabel(usr.subscription_plan)}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <span
                              className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(getDerivedPaymentStatus(usr))}`}
                            >
                              {getStatusLabel(getDerivedPaymentStatus(usr))}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span className="text-sm font-medium">
                              {usr.property_count}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span className="text-sm font-medium">
                              {usr.development_count}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-xs text-muted-foreground flex items-center gap-2">
                              <ShieldCheck className="w-3.5 h-3.5" />
                              Rol y plan fijos
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            {usr.role === "admin" ? (
                              <span className="text-xs text-muted-foreground">Protegido</span>
                            ) : usr.is_archived ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRestoreUser(usr.id)}
                                className="gap-1"
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                                Restaurar
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleArchiveUser(usr.id)}
                                className="gap-1"
                              >
                                <Archive className="w-3.5 h-3.5" />
                                Archivar
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div
                className="flex items-center justify-between mt-6"
                data-testid="admin-pagination"
              >
                <p className="text-sm text-muted-foreground">
                  {totalUsers} usuarios total · Página {currentPage} de{" "}
                  {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                    data-testid="admin-pagination-prev"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((p) => p + 1)}
                    data-testid="admin-pagination-next"
                  >
                    Siguiente <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default AdminUsers;
